import {defineStore} from "pinia";
import {createOrderAnonApi, createOrderApi, getCartApi, updateCartItemsApi,} from "../api/cartApi";
import {getAddressListApi} from "@/api/shippingApi";
import {showError} from "../services/alertService";
import {createCartItem} from "../helpers/cartHelper";
import {useAuthStore} from "./authStore";
import {createPaymentIntent, getPaymentAccounts, orderPayBySchoolPaymentApi,} from "@/api/paymentApi";
import {getProductAmountsByPriceIdsApi} from "@/api/productApi";

export const useCartStore = defineStore("cartStore", {
  state: () => ({
    cart: {
      items: [],
      totalPrice: {
        value: 0,
        currencyIso: "USD",
      },
      subTotal: {
        value: 0,
        currencyIso: "USD",
      },
    },
    checkoutInfo: {},
    deliveryPoints: [],
    deliveryPointAnon: {},
    orderComment: "",
    paymentAccounts: null,
    applySteps: {
      contactInfo: false,
      deliveryInfo: false,
    },
    paymentMethods: [
      // {
      //   id: 1,
      //   isSelected: false,
      //   isAvalible: false,
      //   image: "/img/payment-methods/sss-pay.png",
      //   code: "MONOLITH",
      // },
      // {
      //   id: 2,
      //   isSelected: false,
      //   isAvalible: false,
      //   image: "/img/payment-methods/invoice.png",
      //   code: "CASHLESS",
      // },
      {
        id: 3,
        isSelected: true,
        isAvalible: true,
        image: "/img/payment-methods/stripe.png",
        code: "STRIPE",
      },
      // {
      //   id: 4,
      //   isSelected: false,
      //   isAvalible: false,
      //   image: "/img/payment-methods/interac.png",
      //   code: "INTERAC",
      // },
    ],
  }),

  getters: {
    getCart(store) {
      return store.cart;
    },

    getItemsAmount(store) {
      if (store.cart) return store.cart.items.length;
      return 0;
    },

    getCheckedItmesAmount(store) {
      if (!store.cart) return 0;
      return store.cart.items.filter((item) => item.ready2Order).length;
    },

    getOrderList(store) {
      if (!store.cart) return [];
      return store.cart.items.filter((item) => item.ready2Order);
    },

    getTotalPrice(store) {
      if (!store.cart) return 0;
      return store.cart.totalPrice;
    },

    getDeliveryPoints(store) {
      if (!store.deliveryPoints.length) return [];
      const list = store.deliveryPoints;
      const isMainExists = list.find((point) => point.isMain);

      if (!isMainExists) {
        list[0].isMain = true;
      }

      return list;
    },

    getCheckoutInfo(store) {
      return store.checkoutInfo;
    },

    getDeliveryPointAnon(store) {
      return store.deliveryPointAnon;
    },

    getOrderComment(store) {
      return store.orderComment;
    },

    getPaymentMethods(store) {
      return store.paymentMethods;
    },

    getPaymnetsAccounts(store) {
      return store.paymentAccounts;
    },

    getSelectedPaymentMethod(store) {
      return store.paymentMethods.find((m) => m.isSelected);
    },

    getStepsStatuses(store) {
      return store.applySteps;
    },

    getSelectedAccount(store) {
      if (!store.paymentAccounts) return null;
      return store.paymentAccounts.find((a) => a.isSelected);
    },

    isHasEnoughBalance() {
      if (!this.getTotalPrice || !this.getSelectedAccount) return false;
      return this.getTotalPrice.value <= this.getSelectedAccount.balance;
    },
  },

  actions: {
    async getProductsAmounts() {
      try {
        const priceIds = this.cart.items.map((item) => item.priceId);
        if (!priceIds.length) return;
        const amountsList = await getProductAmountsByPriceIdsApi(
          priceIds.join(",")
        );
        this.cart.items = this.cart.items.map((item) => {
          const foundedAmount = amountsList.find(
            (l) => l.priceId === item.priceId
          );
          if (foundedAmount) {
            return {
              ...item,
              ...foundedAmount,
              ready2Order:
                foundedAmount.restAmount === 0 ? false : item.ready2Order,
            };
          }
          return item;
        });
        this.updateCart(this.cart.items);
      } catch (error) {
        showError(error.message);
        throw error;
      }
    },

    getProductQuantity(priceId) {
      const product = this.cart.items.find((item) => item.priceId === priceId);

      if (!product) return 0;
      return product.quantity;
    },

    setReadyStatus(priceId, value) {
      if (!this.cart) return;
      const items = this.cart.items.map((item) =>
        item.priceId === priceId ? { ...item, ready2Order: value } : item
      );

      this.updateCart(items);
    },

    applyDeliverStep() {
      this.applySteps = { ...this.applySteps, deliveryInfo: true };
    },

    applyContactStep() {
      this.applySteps = { ...this.applySteps, contactInfo: true };
    },

    clearStepsStatuses() {
      this.applySteps = { deliveryInfo: false, contactInfo: false };
    },

    selectAllItems() {
      const items = this.cart.items.map((item) => ({
        ...item,
        ready2Order: true,
      }));

      this.updateCart(items);
    },

    unSelectAllItems() {
      const items = this.cart.items.map((item) => ({
        ...item,
        ready2Order: false,
      }));

      this.updateCart(items);
    },

    increaseQuantity(priceId) {
      if (!this.cart) return;
      const items = this.cart.items.map((item) =>
        item.priceId === priceId
          ? {
              ...item,
              quantity: item.quantity + 1,
              totalPrice: {
                currencyIso: item.totalPrice.currencyIso,
                value: Number(
                  (item.price.value * (item.quantity + 1)).toFixed(2)
                ),
              },
            }
          : item
      );

      this.updateCart(items);
    },

    decreaseQuantity(priceId) {
      if (!this.cart) return;
      const items = this.cart.items.map((item) =>
        item.priceId === priceId && item.quantity > 1
          ? {
              ...item,
              quantity: item.quantity - 1,
              totalPrice: {
                currencyIso: item.totalPrice.currencyIso,
                value: Number(
                  (item.price.value * (item.quantity - 1)).toFixed(2)
                ),
              },
            }
          : item
      );

      this.updateCart(items);
    },

    removeSelectedItems() {
      if (!this.cart) return;

      const items = this.cart.items.filter((item) => !item.ready2Order);
      this.updateCart(items);
    },

    removeItem(priceId) {
      if (!this.cart) return;
      const items = this.cart.items.filter((item) => item.priceId !== priceId);

      this.updateCart(items);
    },

    removeAllCheckedItems() {
      if (!this.cart) return;
      const items = this.cart.items.filter((item) => !item.ready2Order);

      this.updateCart(items);
    },

    calculateSummary(items) {
      const total = items.reduce((acc, item) => {
        if (item.ready2Order && item.restAmount > 0) {
          acc += item.totalPrice.value;
          return acc;
        }
        return acc;
      }, 0);

      return {
        subTotal: {
          value: Number(total.toFixed(2)),
          currencyIso: this.cart.subTotal.currencyIso,
        },
        totalPrice: {
          value: Number(total.toFixed(2)),
          currencyIso: this.cart.totalPrice.currencyIso,
        },
      };
    },

    async updateCart(items) {
      const { subTotal, totalPrice } = this.calculateSummary(items);
      const newCartProps = { items, subTotal, totalPrice };
      this.cart = { ...this.cart, ...newCartProps };

      if (!useAuthStore().isLogined) {
        window.localStorage.setItem("cart", JSON.stringify(this.cart));
        return;
      }
      try {
        await updateCartItemsApi(items);
      } catch (error) {
        showError(error);
        this.loadCart();
      }
    },

    updateLocalCart() {
      const localCart = JSON.parse(window.localStorage.getItem("cart"));
      if (!localCart) return;
      const newCart = localCart.items.filter(
        (cartItem) => !cartItem.ready2Order
      );
      this.updateCart(newCart);
    },

    getItemBySku(sku) {
      if (!this.cart) return false;
      const product = this.cart.items.find((item) => item.sku === sku);
      return product?.quantity || 0;
    },

    itemIsExists(priceId) {
      if (!this.cart) return false;

      const isExists = !!this.cart.items.find(
        (item) => item.priceId === priceId
      );

      return isExists;
    },

    async syncCarts() {
      const remoteCart = await getCartApi();
      const localCart = window.localStorage.getItem("cart");
      const parsedLocalCart = JSON.parse(localCart);

      if (!parsedLocalCart) {
        this.cart = remoteCart;
        return;
      }
      await updateCartItemsApi(parsedLocalCart.items);

      const filteredItems = [
        ...parsedLocalCart.items,
        ...remoteCart.items,
      ].reduce((acc, cartItem) => {
        if (!acc[cartItem.priceId])
          return { ...acc, [cartItem.priceId]: cartItem };
        return { ...acc };
      }, {});

      const finalItems = Object.values(filteredItems);
      const { subTotal, totalPrice } = this.calculateSummary(finalItems);
      this.cart = {
        items: finalItems,
        subTotal,
        totalPrice,
      };
      window.localStorage.setItem("cart", null);
    },

    async loadCart() {
      try {
        if (useAuthStore().isLogined) {
          this.cart = await getCartApi();
        }
      } catch (error) {
        showError(error);
      }
    },

    async loadLocalCart() {
      try {
        const cartInStorage = window.localStorage.getItem("cart");

        if (!cartInStorage || !JSON.parse(cartInStorage)) return;
        const parsedCart = JSON.parse(cartInStorage);
        this.updateCart(parsedCart.items);
      } catch (error) {
        showError(error);
      }
    },

    async loadDeliveryPoints() {
      try {
        if (!useAuthStore().isLogined) {
          this.deliveryPoints = [];
          return;
        }
        this.deliveryPoints = await getAddressListApi();
      } catch (error) {
        showError(error);
      }
    },

    async loadPaymentAccounts() {
      if (!useAuthStore().isLogined) {
        this.paymentAccounts = [];
        return;
      }
      try {
        const accounts = await getPaymentAccounts();

        if (!accounts.length) {
          this.paymentAccounts = [];
          return;
        }

        this.paymentAccounts = accounts.map((a) => {
          return {
            ...a,
            isSelected: false,
          };
        });

        this.paymentAccounts[0].isSelected = true;
      } catch (error) {
        showError(error);
      }
    },

    addToCart({ product, offer }) {
      if (this.itemIsExists(offer.id)) {
        this.increaseQuantity(offer.id);
        return;
      }
      const item = createCartItem(product, offer);

      const items = [...this.cart.items, item];

      this.updateCart(items);
    },

    choosePaymentMethodById(id) {
      this.paymentMethods = this.paymentMethods.map((m) => {
        return {
          ...m,
          isSelected: m.id === id && m.isAvalible,
        };
      });
    },

    choosePaymentAccount(account) {
      this.paymentAccounts = this.paymentAccounts.map((a) => {
        return {
          ...a,
          isSelected: a.billId === account.billId,
        };
      });
    },

    addCheckoutInfo(info) {
      this.checkoutInfo = { ...info };
      this.applyContactStep();
    },

    setAnonDeliveryPoint(address) {
      this.deliveryPointAnon = {
        regionCode: address.region.domesticRegionCode,
        address: address.address,
        name: address.name,
        city: address.city,
        countryIso: address.country.countryIso,
        postCode: address.index,
      };
      this.applyDeliverStep();
    },

    setMainDeliveryPoint(code) {
      this.deliveryPoints = this.deliveryPoints.map((point) => {
        return {
          ...point,
          isMain: point.code === code ? true : false,
        };
      });
    },

    setOrderComment(comment) {
      this.orderComment = comment;
    },

    async createOrderByAnon(googleResponseToken) {
      try {
        const orderList = this.getOrderList;
        const order = {
          details: this.checkoutInfo,
          deliveryPoint: this.deliveryPointAnon,
          comment: this.orderComment,
          paymentSystemCode: this.getSelectedPaymentMethod.code,
          orderItems: orderList.map(({ priceId, quantity }) => ({
            priceId,
            quantity,
          })),
          googleResponseToken,
        };

        const { orderNum, checkoutJwt } = await createOrderAnonApi(order);
        return { orderNum, checkoutJwt };
      } catch (error) {
        showError(error.message);
      }
    },

    async createOrder(googleResponseToken = null) {
      if (!useAuthStore().isLogined)
        return await this.createOrderByAnon(googleResponseToken);
      try {
        const order = {
          details: this.checkoutInfo,
          deliveryPoint: this.deliveryPoints.find((point) => point.isMain),
          comment: this.orderComment,
          paymentSystemCode: this.getSelectedPaymentMethod.code,
        };
        const orderNum = await createOrderApi(order);
        return orderNum;
      } catch (error) {
        showError(error.message);
      }
    },

    async crateStripePay(orderNum, jwt = null) {
      try {
        const order = {
          requestBodyDto: {
            orderNum,
            summ: this.getCart.totalPrice.value,
            pmtCurrency: this.getCart.totalPrice.currencyIso,
            pmtNote: this.orderComment,
          },
          version: "1",
        };
        const checkoutUrl = await createPaymentIntent(order, jwt);
        return checkoutUrl;
      } catch (error) {
        showError(error.message);
      }
    },

    async orderPayBySchoolPayment(orderNum) {
      if (!this.isHasEnoughBalance) return;
      try {
        await orderPayBySchoolPaymentApi(
          orderNum,
          this.getSelectedAccount.billId
        );
      } catch (error) {
        showError(error);
      }
    },
  },
});
